import React, { useState, useMemo, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Activity,
  BarChart2,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  Search,
  Check,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts";
import {
  useUnitWiseGraph,
  useTerritoryPremiseStats,
} from "@features/onboarding/utility-setup/hooks";
import { useUtilityServices } from "@features/cx/consumerOnboarding/hooks";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { PageHeader } from "@shared/components/PageHeader";

const InlineSearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = "Select service...",
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(searchQuery.toLowerCase().trim()),
    );
  }, [options, searchQuery]);

  return (
    <div
      className="relative inline-block text-left min-w-[200px]"
      ref={dropdownRef}
    >
      {/* Inline Search Input Trigger */}
      <div className="relative flex items-center">
        <input
          type="text"
          placeholder={placeholder}
          value={isOpen ? searchQuery : value}
          onFocus={() => {
            setIsOpen(true);
            setSearchQuery("");
          }}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          className="w-full pl-3.5 pr-8 py-2 text-sm font-medium text-gray-800 bg-white border border-gray-300 rounded-lg shadow-2xs hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-10 cursor-text"
        />
        <ChevronDown
          onClick={() => setIsOpen(!isOpen)}
          className={`w-4 h-4 text-gray-500 absolute right-2.5 pointer-events-none transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {/* Scrollable Dropdown List */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1.5 space-y-0.5">
          <div className="max-h-56 overflow-y-auto space-y-0.5 pr-0.5">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <div
                    key={opt.value}
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                      setSearchQuery("");
                    }}
                    className={`px-3 py-2 text-sm rounded-lg flex items-center justify-between cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <span>{opt.label}</span>
                    {isSelected && (
                      <Check className="w-4 h-4 text-blue-600 shrink-0" />
                    )}
                  </div>
                );
              })
            ) : (
              <div className="py-3 text-center text-xs text-gray-400 font-medium">
                No matching options
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CustomDot = (props: any) => {
  const { cx, cy } = props;
  if (!cx || !cy) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={5}
      fill="#ffffff"
      stroke="#2563eb"
      strokeWidth={3}
    />
  );
};

export const Summary: React.FC = () => {
  const { code } = useParams<{ code?: string }>();
  const decodedCode = code ? decodeURIComponent(code) : "";
  const { data: statsData } = useTerritoryPremiseStats(decodedCode);

  const rawUnit = statsData?.basicInfo?.name || decodedCode || "";
  const unitName = useMemo(() => {
    if (rawUnit.includes("#")) {
      const parts = rawUnit.split("_");
      const lastPart = parts[parts.length - 1];
      return lastPart.split("#")[0];
    }
    return rawUnit;
  }, [rawUnit]);

  const { data: utilityData } = useUtilityServices();
  const [selectedService, setSelectedService] = useState<string>("Water");

  const { data: graphResponse } = useUnitWiseGraph(
    unitName,
    selectedService,
    true,
    !!unitName,
  );

  const getUtilityOptions = useMemo(
    () =>
      utilityData
        ? utilityData
            .filter((utility: any) => utility.isActive === true)
            .map((utility: any) => ({
              value: utility.name,
              label: utility.name,
            }))
        : [],
    [utilityData],
  );

  const utilityOptions = getUtilityOptions;

  const result = graphResponse?.result;

  const unitLabel = useMemo(() => {
    const s = selectedService.toLowerCase();
    if (s.includes("water")) return "gal";
    if (s.includes("electric")) return "kWh";
    if (s.includes("gas")) return "ccf";
    if (s.includes("solar")) return "kWh";
    return "units";
  }, [selectedService]);

  const currentUsageNum = result?.currentMonthUsage ?? 0;
  const avgUsageNum = result?.averageMonthlyUsage ?? 0;
  const highestUsageNum = result?.highestMonthlyUsage ?? 0;
  const lowestUsageNum = result?.lowestMonthlyUsage ?? 0;

  const kpis = [
    {
      title: "Current Month Usage",
      count: `${currentUsageNum.toLocaleString()} ${unitLabel}`,
      trendSummary: `${selectedService} consumption`,
      icon: <Activity className="w-5 h-5 text-gray-500" />,
    },
    {
      title: "Average Monthly Usage",
      count: `${avgUsageNum.toLocaleString()} ${unitLabel}`,
      trendSummary: "Historical average",
      icon: <BarChart2 className="w-5 h-5 text-gray-500" />,
    },
    {
      title: "Highest Monthly Usage",
      count: `${highestUsageNum.toLocaleString()} ${unitLabel}`,
      trendSummary: "Peak consumption",
      icon: <TrendingUp className="w-5 h-5 text-gray-500" />,
    },
    {
      title: "Lowest Monthly Usage",
      count: `${lowestUsageNum.toLocaleString()} ${unitLabel}`,
      trendSummary: "Minimum consumption",
      icon: <TrendingDown className="w-5 h-5 text-gray-500" />,
    },
  ];

  const chartData = useMemo(() => {
    const records = result?.records;
    if (!records || Object.keys(records).length === 0) {
      return [];
    }

    const MONTH_ORDER = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const months = Object.keys(records);
    months.sort((a, b) => {
      const idxA = MONTH_ORDER.indexOf(a);
      const idxB = MONTH_ORDER.indexOf(b);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      return 0;
    });

    return months.map((m) => ({
      month: m,
      usage: records[m]?.consumption ?? 0,
    }));
  }, [result]);

  return (
    <div className="space-y-6">
      {/* Top Header & Dropdown */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <PageHeader
            title="Usage Summary"
            subtitle="Monthly consumption KPIs"
          />
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700">
            Utility Service
          </span>
          <InlineSearchableSelect
            options={utilityOptions}
            value={selectedService}
            onChange={setSelectedService}
            placeholder="Select service..."
          />
        </div>
      </div>

      {/* 4 KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, index) => (
          <QuickStatsCard
            key={index}
            title={kpi.title}
            count={kpi.count}
            trendSummary={kpi.trendSummary}
            icon={kpi.icon}
          />
        ))}
      </div>

      {/* Consumption Trend Chart Card */}
      <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-2xs space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-gray-900">
              Consumption Trend
            </h3>
            <p className="text-xs text-gray-500">
              Last 6 months · {selectedService}
            </p>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-100 inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
            {selectedService}
          </span>
        </div>

        {/* Unit label above Y-axis */}
        <div className="text-xs font-medium text-gray-400 pl-2 -mb-2">
          {unitLabel}
        </div>

        {/* Chart */}
        <div className="h-72 w-full pt-1">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 35, right: 35, left: 10, bottom: 10 }}
            >
              <defs>
                <linearGradient id="colorUsage" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f1f5f9"
              />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#64748b", fontSize: 12, fontWeight: 500 }}
                dy={10}
                padding={{ left: 30, right: 30 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                tickFormatter={(val) =>
                  `${val >= 1000 ? (val / 1000).toFixed(1) + "k" : val}`
                }
                domain={["dataMin - 150", "dataMax + 250"]}
                width={45}
              />
              <Tooltip
                formatter={(value: any) => [
                  `${Number(value).toLocaleString()} ${unitLabel}`,
                  "Usage",
                ]}
                contentStyle={{
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              />
              <Area
                type="monotone"
                dataKey="usage"
                stroke="#3b82f6"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorUsage)"
                dot={<CustomDot />}
              >
                <LabelList
                  dataKey="usage"
                  position="top"
                  formatter={(val: any) => `${Number(val).toLocaleString()}`}
                  style={{
                    fill: "#2563eb",
                    fontSize: "12px",
                    fontWeight: "700",
                  }}
                  offset={10}
                />
              </Area>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Summary;
